import type { ReactNode } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useEffect, useMemo } from 'react';
import { useMe } from '../hooks/useMe.js';
import { api } from '../lib/api.js';
import { initFeedbackWidget } from '../lib/feedback.js';
import { LANGUAGES, type LanguageCode } from '../i18n/index.js';

interface NavItem {
  to: string;
  key: string;
  icon: string;
  module?: 'cleanops' | 'pantry' | 'platform' | 'agent';
  badgeKey?: 'openWorkOrders' | 'ordersPending';
}

const NAV: NavItem[] = [
  { to: '/dashboard', key: 'dashboard', icon: '⌂' },
  { to: '/cleanops', key: 'cleanops', icon: '🧹', module: 'cleanops', badgeKey: 'openWorkOrders' },
  { to: '/pantry', key: 'pantry', icon: '☕', module: 'pantry', badgeKey: 'ordersPending' },
  { to: '/platform', key: 'platform', icon: '📋', module: 'platform' },
  { to: '/agent', key: 'agent', icon: '🤖', module: 'agent' },
];

const ADMIN_NAV: NavItem[] = [
  { to: '/admin/users', key: 'users', icon: '👥' },
  { to: '/admin/pantry-orders', key: 'orders', icon: '📋' },
  { to: '/admin/audit', key: 'audit', icon: '📜' },
];

// Header titles per route section (mirrors the mockup's contextual header).
const HEADER_TITLES: Record<string, { key: string; sub: string }> = {
  '/dashboard': { key: 'nav.dashboard', sub: 'admin_hub.dashboard' },
  '/cleanops': { key: 'nav.cleanops', sub: 'janitorial.workflows' },
  '/pantry': { key: 'nav.pantry', sub: 'beverage.ordering' },
  '/platform': { key: 'nav.platform', sub: 'events.budget.tasks' },
  '/agent': { key: 'nav.agent', sub: 'ai.assistant' },
  '/admin/users': { key: 'nav.users', sub: 'admin.user_management' },
  '/admin/audit': { key: 'nav.audit', sub: 'admin.audit_timeline' },
  '/admin/pantry-orders': { key: 'nav.orders', sub: 'admin.pantry_orders' },
};

export function Shell() {
  const { t, i18n } = useTranslation();
  const { data: me, isLoading, error } = useMe();
  const nav = useNavigate();
  const loc = useLocation();

  useEffect(() => {
    if (!isLoading && error) nav('/login', { replace: true });
  }, [isLoading, error, nav]);

  // Unified Feedback widget — mounted once for the whole authenticated app.
  // initFeedbackWidget guards against re-init itself, and never throws.
  useEffect(() => {
    if (me) initFeedbackWidget(i18n.language);
  }, [me, i18n.language]);

  const visibleNav = useMemo(
    () =>
      (me ? NAV : []).filter((item) => {
        if (!item.module) return true;
        const perms = me?.permissions[item.module] ?? [];
        return perms.includes('view') || perms.includes('create');
      }),
    [me],
  );
  const showAdmin = me?.role === 'super_admin' || me?.role === 'admin';

  const initials = useMemo(() => {
    const parts = (me?.displayName ?? '').trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return me?.email?.[0]?.toUpperCase() ?? 'U';
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }, [me]);

  const headerKey = useMemo(() => {
    const match = Object.keys(HEADER_TITLES).find((p) => loc.pathname.startsWith(p));
    return match ? HEADER_TITLES[match] : { key: 'nav.dashboard', sub: 'admin_hub' };
  }, [loc.pathname]);

  async function signOut() {
    await api.post('/api/v1/auth/logout').catch(() => null);
    window.location.href = '/login';
  }

  if (isLoading) {
    return (
      <div className="flex h-screen w-screen items-center justify-center text-text-secondary">
        {t('common.loading')}
      </div>
    );
  }
  if (!me) return null;

  return (
    <div className="flex h-screen w-screen overflow-hidden">
      <aside
        className="flex flex-shrink-0 flex-col border-e border-white/5"
        style={{ width: 260, background: 'var(--bg-sidebar)' }}
      >
        {/* Brand */}
        <div className="border-b border-white/5 px-5 pb-5 pt-6">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-[10px] bg-gradient-to-br from-gold to-gold-light text-[18px] font-extrabold text-[#0a0d14]">
              U
            </div>
            <div className="min-w-0">
              <div className="truncate text-[15px] font-bold tracking-tight">URED Admin Hub</div>
              <div className="font-mono text-[11px] text-text-muted">v0.1 · UAE North</div>
            </div>
          </div>
        </div>

        {/* Nav */}
        <nav className="flex-1 overflow-y-auto p-3">
          <SectionLabel>{t('nav.section.modules', { defaultValue: 'Modules' })}</SectionLabel>
          {visibleNav.map((item) => (
            <ShellNavLink key={item.to} item={item} />
          ))}

          {showAdmin ? (
            <>
              <SectionLabel>{t('nav.section.admin', { defaultValue: 'Admin' })}</SectionLabel>
              {ADMIN_NAV.map((item) => (
                <ShellNavLink key={item.to} item={item} />
              ))}
            </>
          ) : null}
        </nav>

        {/* User card */}
        <div className="border-t border-white/5 p-3">
          <button
            type="button"
            onClick={signOut}
            className="flex w-full items-center gap-2.5 rounded-lg bg-bg-card p-2.5 text-start transition hover:bg-bg-card-hover"
            aria-label={t('nav.signOut')}
          >
            <div
              className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-gold to-gold-light text-[13px] font-bold text-[#0a0d14]"
            >
              {initials}
            </div>
            <div className="min-w-0 flex-1">
              <div className="truncate text-[13px] font-semibold">{me.displayName}</div>
              <div className="truncate text-[11px] text-text-muted">{me.role}</div>
            </div>
            <span className="text-xs text-text-muted" title={t('nav.signOut')}>
              ↩
            </span>
          </button>
        </div>
      </aside>

      <main className="flex flex-1 flex-col overflow-hidden">
        {/* Header */}
        <header
          className="flex flex-shrink-0 items-center gap-4 border-b border-white/5 px-6"
          style={{ height: 64, background: 'var(--bg-secondary)' }}
        >
          <div className="flex-1 min-w-0">
            <h3 className="truncate text-[17px] font-bold tracking-tight">
              {t(headerKey.key, { defaultValue: headerKey.key.split('.').pop() })}
            </h3>
            <p className="truncate font-mono text-xs text-text-muted">
              {headerKey.sub}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <select
              value={i18n.language}
              onChange={(e) => i18n.changeLanguage(e.target.value as LanguageCode)}
              className="rounded-lg border border-white/10 bg-transparent px-3 py-2 text-xs text-text-secondary transition hover:border-gold/40 hover:text-gold"
              aria-label={t('common.language')}
            >
              {LANGUAGES.map((l) => (
                <option key={l.code} value={l.code}>
                  {l.label}
                </option>
              ))}
            </select>
            <button
              type="button"
              className="flex h-9 w-9 items-center justify-center rounded-lg border border-white/10 text-text-secondary transition hover:border-gold/40 hover:text-gold"
              title="Notifications"
              aria-label="Notifications"
            >
              🔔
            </button>
            <button
              type="button"
              onClick={signOut}
              className="rounded-lg border border-white/10 px-3 py-2 text-xs text-text-secondary transition hover:border-gold/40 hover:text-gold"
            >
              {t('nav.signOut')}
            </button>
          </div>
        </header>
        <div className="flex-1 overflow-auto p-6">
          <Outlet />
        </div>
      </main>
    </div>
  );
}

function SectionLabel({ children }: { children: ReactNode }) {
  return (
    <div className="mb-2 mt-4 px-2 text-[10px] font-bold uppercase tracking-[0.12em] text-text-muted first:mt-0">
      {children}
    </div>
  );
}

function ShellNavLink({ item }: { item: NavItem }) {
  const { t } = useTranslation();
  return (
    <NavLink
      to={item.to}
      className={({ isActive }: { isActive: boolean }) =>
        `mb-0.5 flex items-center gap-3 rounded-lg px-3 py-2.5 text-[14px] font-medium transition ${
          isActive
            ? 'text-gold'
            : 'text-text-secondary hover:bg-bg-card hover:text-text'
        }`
      }
      style={({ isActive }: { isActive: boolean }) =>
        isActive
          ? {
              background: 'var(--gold-dim)',
              border: '1px solid var(--border-gold)',
            }
          : undefined
      }
    >
      {({ isActive }: { isActive: boolean }) => (
        <>
          <span
            className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg text-base"
            style={{
              background: isActive ? 'rgba(212,175,55,0.15)' : 'rgba(255,255,255,0.04)',
            }}
          >
            {item.icon}
          </span>
          <span className="flex-1 truncate">{t(`nav.${item.key}`, { defaultValue: item.key })}</span>
        </>
      )}
    </NavLink>
  );
}
